import type { components } from "@/services/policy-schema";
import { faker } from "@faker-js/faker";

const generateMockUser: () => components["schemas"]["User"] = () => ({
  id: faker.string.uuid(),
  email: faker.internet.email(),
});

type PolicyType = components["parameters"]["PolicyTypeFilterParam"];
type Outcome = "PENDING" | "ACCEPTED" | "REJECTED";

export const generateMockApproval: (
  query?: URLSearchParams,
) => components["schemas"]["Approval"] = (query) => {
  const statusFilters = (query?.getAll("approvalStatus") ?? [
    "PENDING",
    "APPROVED",
    "REJECTED",
  ]) as components["parameters"]["ApprovalStatusFilterParam"];

  const policyTypeQuery = query?.get("policyType") as PolicyType | undefined;
  const policyTypeFilters = policyTypeQuery
    ? [policyTypeQuery]
    : ([
        "EXTERNAL_ADDRESS_ADDED",
        "EXTERNAL_ADDRESS_AWAITING_VERIFICATION",
        "WITHDRAWAL_REQUESTED",
        "EXTERNAL_ADDRESS_REVOKE_REQUESTED",
        "EXTERNAL_ADDRESS_UPDATE_REQUESTED",
      ] as PolicyType[]);

  const approvalStatus = faker.helpers.arrayElement(statusFilters);

  let userVotingStatus = undefined;
  switch (approvalStatus) {
    case "PENDING":
      userVotingStatus = faker.helpers.arrayElement([
        "PENDING",
        "USER_TRIGGERED",
      ]);
      break;
    case "APPROVED":
      userVotingStatus = faker.helpers.arrayElement([
        "PENDING",
        "APPROVED",
        "USER_TRIGGERED",
      ]);
      break;
    case "REJECTED":
      userVotingStatus = faker.helpers.arrayElement([
        "PENDING",
        "APPROVED",
        "REJECTED",
        "USER_TRIGGERED",
      ]);
      break;
  }
  let outcome: Outcome = "PENDING";
  if (approvalStatus === "APPROVED") {
    outcome = "ACCEPTED";
  }
  if (approvalStatus === "REJECTED") {
    outcome = "REJECTED";
  }

  return {
    id: faker.string.uuid(),
    type: faker.helpers.arrayElement(policyTypeFilters),
    description: faker.lorem.sentence(12),
    requester: generateMockUser(),
    createdAt: faker.date.recent().getTime(),
    updatedAt: faker.date.recent().getTime(),
    canActOnApproval: faker.datatype.boolean(),
    approvalStatus,
    userVotingStatus,
    evaluationDetails: {
      outcome,
      reason: faker.lorem.sentence(),
      decidedAt: faker.date.recent().getTime(),
      metadata: getRandomMetadata(),
    },
  };
};

const propertyNames = [
  "address",
  "email",
  "city",
  "phone",
  "company",
  "blockchain",
  "account",
];

export type SortableField =
  | "policyType"
  | "datetime"
  | "updatedAt"
  | "approvalStatus"
  | "type"
  | "createdAt";
export type SortOrder = "asc" | "desc";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortPolicyInstances<T extends Record<string, any>>(
  items: T[],
  field: SortableField,
  order: SortOrder = "asc",
): T[] {
  if (field === "policyType") {
    field = "type";
  } else if (field === "datetime") {
    field = "createdAt";
  }

  return [...items].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];

    let comparison: number;

    if (field === "createdAt" || field === "updatedAt") {
      comparison = (aValue || 0) - (bValue || 0);
    } else {
      comparison = String(aValue || "").localeCompare(String(bValue || ""));
    }

    return order === "asc" ? comparison : -comparison;
  });
}

function getRandomMetadata() {
  return Object.fromEntries(
    faker.helpers
      .arrayElements(
        propertyNames,
        faker.number.int({ min: 1, max: propertyNames.length }),
      )
      .map((prop) => [prop, faker.lorem.words({ min: 1, max: 3 })]),
  );
}

const approvalDetail: components["schemas"]["ApprovalDetail"] = {
  id: "abc123",
  type: "EXTERNAL_ADDRESS_ADDED",
  description:
    "User bob@lmax.com wants to add address 7xKXtg2CW87d8V6zXKBq7cM8Y2FPHwzaAkxqEqD7x4rH",
  approvalStatus: "PENDING",
  createdAt: 1764011501851,
  updatedAt: 1764011601851,
  requester: {
    id: "13",
    email: "bob@lmax.com",
  },
  customer: {
    id: "cust-1234",
    name: "customer1234",
  },
  canActOnApproval: true,
  requiredApprovalRoles: [
    {
      roleId: "cfo",
      roleName: "Chief Financial Officer",
      requiredApprovals: 2,
      currentApprovals: 1,
    },
    {
      roleId: "ceo",
      roleName: "Chief Executive Officer",
      requiredApprovals: 1,
      currentApprovals: 1,
    },
  ],
  evaluationDetails: {
    outcome: "PENDING",
    reason: "Awaiting: 1 pending of 1 conditions",
    metadata: {
      metadataType: "com.lmax.custodian.policy.condition.CompoundMetadata",
      outcomes: [
        {
          outcome: "ACCEPTED",
          reason: "Value within threshold",
          metadata: {
            metadataType:
              "com.lmax.custodian.policy.condition.ThresholdCondition$Metadata",
            minimum: 0,
            maximum: 1000,
            value: 25,
          },
        },
      ],
    },
  },
  history: {
    events: [
      {
        timestamp: 1764011501851,
        description: "Created instance abc123",
      },
    ],
  },
};

// TO-DO: create dynamic approval detail
export const approvalResponse = approvalDetail;
